import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface Campaign {
  id: string;
  title: string;
  total_days: number;
  send_time: string;
  status: 'active' | 'paused' | 'completed';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface CampaignContextType {
  campaigns: Campaign[];
  selectedCampaignId: string | null;
  setSelectedCampaignId: (id: string) => void;
  selectedCampaign: Campaign | null;
  isLoading: boolean;
}

const CampaignContext = createContext<CampaignContextType | undefined>(undefined);

export function CampaignProvider({ children }: { children: React.ReactNode }) {
  const [selectedCampaignId, setSelectedCampaignId] = useState<string | null>(null);

  const { data: campaigns = [], isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading campaigns:', error);
        throw error;
      }

      return data as Campaign[];
    },
  });

  // Default to active campaign on load
  useEffect(() => {
    if (campaigns.length > 0 && !selectedCampaignId) {
      const activeCampaign = campaigns.find((c) => c.is_active);
      if (activeCampaign) {
        setSelectedCampaignId(activeCampaign.id);
      } else {
        // If no active campaign, default to first campaign
        setSelectedCampaignId(campaigns[0].id);
      }
    }
  }, [campaigns, selectedCampaignId]);

  const selectedCampaign = campaigns.find((c) => c.id === selectedCampaignId) || null;

  return (
    <CampaignContext.Provider
      value={{
        campaigns,
        selectedCampaignId,
        setSelectedCampaignId,
        selectedCampaign,
        isLoading,
      }}
    >
      {children}
    </CampaignContext.Provider>
  );
}

export function useCampaign() {
  const context = useContext(CampaignContext);
  if (context === undefined) {
    throw new Error('useCampaign must be used within a CampaignProvider');
  }
  return context;
}
